import {Component, EventEmitter, HostListener, Input, Output} from '@angular/core';
import {ToolService} from "@services/tool.service";
import {TreeModel} from "@components/tree/tree.model";
import * as _ from 'lodash';
import {ToastsManager} from "@app/shared/util/toastr.service";
import {TranslateService} from "@ngx-translate/core";
import {ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Log} from "ng2-logger";
import {TreeAdapter} from "@app/shared/util/tree-adapter.utils";
import {ScannerDirective} from "@app/shared/directives/scanner.directive";
import {Tool} from "@app/tool/model/tool.model";
import {ToolSet} from "@app/tool/model/toolSet.model";
import {TableConfig} from '@app/shared/util/table-config.utils';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
    selector: 'app-tool-scan-tree',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ToolScanTreeComponent,
            multi: true
        }
    ],
    templateUrl: './tool-scan-tree.component.html',
    styleUrls: ['./tool-scan-tree.component.scss']
})
export class ToolScanTreeComponent implements ControlValueAccessor {
    _logger = Log.create(ToolScanTreeComponent.name);

    @Input() loading = false;
    @Input() showErrors = false;
    @Input() formControl: UntypedFormControl;
    @Input() mode: 'scan' | 'display' | 'displayGroupedByLocation' | 'displayWithStance' = 'display';
    @Input() labelText = '';
    @Input() emptyMessage = '';
    @Input() isMaterial = true;
    @Input() isScanListenerActive = true;
    @Input() parameters = [];
    @Input() onlyOne = false;
    @Input() config: TableConfig;
    @Input() allExpanded = false;

    @Output() toolSelect = new EventEmitter<any>();
    @Output() toolUnselect = new EventEmitter<any>();
    @Output() toolsSelected = new EventEmitter<any>();
    @Output() rowAlertClick = new EventEmitter<any>();
    @Output() leafIconClick = new EventEmitter<any>();

    selectedTools = [];
    focused = true;
    _items: TreeModel[] = [];
    showWarning = false;

    // strobe for scaning functionality to add tools to table on scan
    scanEnabled = false;
    // strobe for inspection mode in which tools are striked out after they are scanned
    inspectEnabled = false;


    private onChangeCallback: (_: any) => {};
    private onTouchedCallback: (_: any) => {};

    constructor(private toolService: ToolService,
                private toastr: ToastsManager,
                private treeAdapter: TreeAdapter,
                private translateService: TranslateService) {
    }

    @HostListener('window:focus', ['$event'])
    onFocus() {
        this.focused = true;
    }

    @HostListener('window:blur', ['$event'])
    onBlur() {
        this.focused = false;
    }

    onScan(toolUid) {
        this.loading = true;
        const dataMatrix = ScannerDirective.UID_REGEX.exec(toolUid);
        const uid = _.toLower(dataMatrix[3]);

        if (this.isToolAlreadyInList(uid)) {
            this._logger.info('onScan:: Tool or set already in list');
            this.toastr.warning(this.buildToolAlreadyExistentMessage(uid));
            this.loading = false;
        } else {
            this._logger.info('onScan:: Retrieving tool or set for uid', uid);
            this.getToolSetForToolUid(uid);
        }
    }

    onToolSelect(event) {
        this._logger.info('onToolSelect:: event', event);
        this.checkUnselectedTools();
        this.emitSelection(event.node);
    }

    onToolUnselect(event) {
        this._logger.info('onToolUnselect:: event', event);
        this.checkUnselectedTools();
        this.emitUnselection(event.node);
    }

    onRowAlertClick(event) {
        this._logger.info('onRowAlertClick:: event', event);
        this.rowAlertClick.emit(event);
    }

    onSelections(selections: TreeModel[]) {
        this._logger.info('onSelections:: List of selected items', selections);
        this.selectedTools = selections;

        if (this.onChangeCallback) {
            this.onChangeCallback(this.selectedTools);
        }
        this.toolsSelected.emit(this.selectedTools);
    }

    get items() {
        return this._items;
    }

    @Input()
    set items(items) {
        switch (this.mode) {
            case 'scan':
                this._items = items;
                this.scanEnabled = true;
                break;
            case 'display':
                this._items = this.treeAdapter.adaptToolsToTree(items, 'default');
                break;
            case 'displayGroupedByLocation':
                this._items = this.treeAdapter.adaptToolsForLocationTree(items, 'default');
                break;
            case 'displayWithStance':
                this._items = this.treeAdapter.adaptToolsToTree(items, 'default', true);
        }

    }

    writeValue(obj: any): void {
        this.selectedTools = obj || [];
    }

    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    shouldRenderScanStatus() {
        return this.mode === 'scan';
    }

    private getToolSetForToolUid(uid: string) {
        this.toolService.getToolSetsByToolUID(uid, this.config).subscribe((data) => {
                this._logger.info('onScan:: API returned:', data);
                let toolOrSet = this._items.find(toolOrSetObj => toolOrSetObj.data.children &&
                    toolOrSetObj.data.children.find(x => x.toolUid === uid && !x.selected));
                if (!toolOrSet) {
                    if (this.onlyOne) {
                        this._items = [];
                        this.selectedTools = []
                    }
                    toolOrSet = this.adaptToToolsSets(data, uid);
                    toolOrSet.expanded = false;
                    this._items.push(toolOrSet);
                }
                this.makeSelections(toolOrSet, uid);
                this.loading = false;
            },
            (error: HttpErrorResponse) => {
                this._logger.info('onScan:: API returned error', error);
                this.loading = false;
                if (error.status === 418) {
                    this.toastr.warning(this.translateService.instant(error.error.message));
                }
            }
        );
    }

    private makeSelections(toolOrSet: TreeModel, uid: string) {
        let selectedItem: TreeModel;
        if (toolOrSet.children && toolOrSet.children.length > 0) {
            selectedItem = _.find(toolOrSet.children, function (item) {
                return item.data.toolUid === uid;
            });
        } else {
            selectedItem = _.find(this._items, function (item) {
                return item.data.toolUid === uid;
            });
        }

        if (this.selectedTools.indexOf(selectedItem) !== -1) {
            this.toastr.warning(this.buildToolAlreadyExistentMessage(selectedItem.data.toolUid));
            return;
        }

        if (selectedItem) {
            this.selectedTools.push(selectedItem);
            if (selectedItem.parent) {
                selectedItem.parent.partialSelected = true;
                if (this.selectedTools.filter(x => x.parent && x.parent.data && x.parent.data.id === selectedItem.parent.data.id).length
                    === selectedItem.parent.children.length) {
                    selectedItem.parent.partialSelected = false;
                    this.selectedTools.push(selectedItem.parent);
                }
            }
            this.emitSelection(selectedItem);
            this.onSelections(this.selectedTools);
        }
    }


    /**
     * Adapts the incoming payload for use with the {@link ToolScanTreeComponent}
     * If the tool is part of a set, it will be displayed as an expandable section.
     * Otherwise, it will be displayed as a single selectable tool.
     *
     * @param data
     * @param uid
     * @returns {TreeModel}
     */
    private adaptToToolsSets(data, uid) {
        if (data.total > 1) {
            return this.buildSelectableSet(data, uid);
        } else {
            return this.buildSelectableTool(data, uid);
        }
    }

    private buildSelectableSet(data, uid) {
        const section = new TreeModel();
        section.label = `${data.items[0].name} [${data.total}]`;
        section.data = new ToolSet({tools: data.items, nrOfTools: data.items.length})
        data.items.forEach((item) => {
            const treeItem = new TreeModel({label: `${item.name} - ${item.toolUid}`});
            treeItem.data = new Tool(item);
            treeItem.type = 'default';
            if (item.toolUid === uid) {
                this.emitSelection(treeItem);
            }
            treeItem.selectable = false;
            section.children.push(treeItem);
        });
        section.selectable = false;
        section.partialSelected = true;
        return section;
    }

    // TODO reuse this in the buildSelectableSet
    private buildSelectableTool(data, uid) {
        const section = new TreeModel();
        section.type = 'tree-model';
        section.label = `${data.items[0].name} -  ${uid}`;
        section.data = data.items[0] as Tool;
        section.selectable = false;
        this.emitSelection(section);
        return section;
    }

    private isToolAlreadyInList(uid) {
        if (this.selectedTools && this.selectedTools.length === 0) {
            return false;
        }
        let result = this.selectedTools.map(x => x.data);

        result = this.liniarizeToolSets(result);

        const finalResult = _.filter(result, {toolUid: uid});

        return finalResult.length > 0;
    }

    private liniarizeToolSets(items) {
        let newDatas = [];
        if (items) {
            items.forEach((item) => {
                if (item instanceof ToolSet) {
                    newDatas = newDatas.concat(item.tools);
                } else {
                    newDatas.push(item);
                }
            });
        }
        return newDatas;
    }

    private buildToolAlreadyExistentMessage(uid) {
        return this.translateService.instant('tree.toolSelect.toasts.alreadyScanned', {uid: uid});
    }

    private emitSelection(change: TreeModel) {
        this._logger.info('emitSelection:: ', change);
        if (this.onChangeCallback) {
            this.onChangeCallback(this.selectedTools);
        }
        this.toolSelect.emit(change);
    }

    private emitUnselection(change: TreeModel) {
        this._logger.info('emitUnselection:: ', change);
        if (this.onChangeCallback) {
            this.onChangeCallback(this.selectedTools);
        }
        this.toolUnselect.emit(change);
    }

    /**
     * Checking if there is any displayed group without any tool selected
     * */
    private checkUnselectedTools() {
        const toolUids = [];

        for (const item of this.selectedTools) {
            toolUids.push(item.data.toolUid);
        }
        /**
         * Creating a set from the UIDs of all displayed tools
         * */
        const displayedTools = new Set();

        for (const treeModel of this._items) {
            if (treeModel.data.children) {
                for (const toolFromSet of treeModel.data.children) {
                    displayedTools.add(toolFromSet.toolUid);
                }
            } else {
                displayedTools.add(treeModel.data.toolUid);
            }

            /**
             * Creating a set containing the intersection of all Displayed UIDs and all SELECTED UIDS from each group. If the size of the
             * intersection is 0 it means that exists a group with unselected items
             * */
            const intersection = new Set([...toolUids].filter(val => displayedTools.has(val)));
            if (intersection.size === 0) {
                this.showWarning = true;
                break;
            } else {
                this.showWarning = false;
            }

            // Resetting the displayedTools Set after each iteration in order to be able to make the intersection of one single group
            displayedTools.clear();
        }
    }

    onLeafIconClick(node: any) {
        this.leafIconClick.emit(node);
    }
}
